import React, { useState, useMemo } from "react";
import { useApp } from "../context/AppContext";
import { formatINR } from "../utils/helpers";
import {
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  TrendingUp, TrendingDown, DollarSign, Users, PieChart as PieIcon,
  BarChart3, Download, Calendar, CreditCard, Landmark, FileText
} from "lucide-react";

const COLORS = ["#F5A623", "#3B82F6", "#10B981", "#EF4444", "#A78BFA", "#22D3EE", "#F97316", "#EC4899"];

const tooltipStyle = {
  contentStyle: { background: "#0F1629", border: "1px solid rgba(255,255,255,.1)", borderRadius: 10, fontSize: 13, fontFamily: "Outfit, sans-serif" },
  labelStyle: { color: "#94A3B8", fontWeight: 600 },
  itemStyle: { color: "#E2E8F0" },
};

function StatCard({ icon, label, value, sub, color }) {
  const c = color || "#F5A623";
  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ width: 42, height: 42, borderRadius: 12, background: c + "18", display: "flex", alignItems: "center", justifyContent: "center", color: c, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, color: "#64748B", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#E2E8F0", fontFamily: "var(--mono)" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 16, fontWeight: 700, color: "#E2E8F0", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
      {children}
    </div>
  );
}

export default function AnalyticsReports() {
  const { expenses, team, payments, loanRequests, mode } = useApp();
  const isAdmin = mode === "admin";
  const [period, setPeriod] = useState("6m");

  // Expense analytics
  const expenseStats = useMemo(() => {
    const total = expenses.reduce((s, e) => s + Number(e.amount), 0);
    const thisMonth = new Date().toISOString().slice(0, 7);
    const monthExpenses = expenses.filter(e => (e.date || "").startsWith(thisMonth));
    const monthTotal = monthExpenses.reduce((s, e) => s + Number(e.amount), 0);
    const lastMonth = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 7);
    const lastMonthTotal = expenses.filter(e => (e.date || "").startsWith(lastMonth)).reduce((s, e) => s + Number(e.amount), 0);
    const change = lastMonthTotal > 0 ? ((monthTotal - lastMonthTotal) / lastMonthTotal * 100).toFixed(1) : 0;
    return { total, monthTotal, lastMonthTotal, change };
  }, [expenses]);

  // Monthly trend data
  const monthlyTrend = useMemo(() => {
    const months = period === "3m" ? 3 : period === "6m" ? 6 : 12;
    const data = [];
    for (let i = months - 1; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const key = d.toISOString().slice(0, 7);
      const label = d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
      const expTotal = expenses.filter(e => (e.date || "").startsWith(key)).reduce((s, e) => s + Number(e.amount), 0);
      const payTotal = payments.filter(p => (p.month || p.paidDate || "").startsWith(key)).reduce((s, p) => s + Number(p.amount), 0);
      data.push({ name: label, expenses: expTotal, payroll: payTotal });
    }
    return data;
  }, [expenses, payments, period]);

  // Category breakdown
  const categoryData = useMemo(() => {
    const map = {};
    expenses.forEach(e => {
      const cat = e.category || "Other";
      map[cat] = (map[cat] || 0) + Number(e.amount);
    });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [expenses]);

  // Payment method breakdown
  const paymentMethodData = useMemo(() => {
    const map = {};
    expenses.forEach(e => {
      const method = e.paymentMethod || "Cash";
      map[method] = (map[method] || 0) + Number(e.amount);
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [expenses]);

  // Team stats
  const teamStats = useMemo(() => {
    const active = team.filter(m => m.status === "Active");
    const totalSalary = active.reduce((s, m) => s + Number(m.salary || 0), 0);
    const avgSalary = active.length > 0 ? totalSalary / active.length : 0;
    const totalPaid = payments.reduce((s, p) => s + Number(p.amount), 0);
    return { activeCount: active.length, totalCount: team.length, totalSalary, avgSalary, totalPaid };
  }, [team, payments]);

  // Loan stats
  const loanStats = useMemo(() => {
    const approved = loanRequests.filter(l => l.status === "Approved");
    const pending = loanRequests.filter(l => l.status === "Pending");
    const denied = loanRequests.filter(l => l.status === "Denied");
    const totalApproved = approved.reduce((s, l) => s + Number(l.amount), 0);
    const totalPending = pending.reduce((s, l) => s + Number(l.amount), 0);
    return { approved: approved.length, pending: pending.length, denied: denied.length, totalApproved, totalPending };
  }, [loanRequests]);

  // Loan status chart data
  const loanChartData = useMemo(() => [
    { name: "Approved", value: loanStats.approved, color: "#10B981" },
    { name: "Pending", value: loanStats.pending, color: "#F5A623" },
    { name: "Denied", value: loanStats.denied, color: "#EF4444" },
  ].filter(d => d.value > 0), [loanStats]);

  // Daily expense data for area chart (last 30 days)
  const dailyExpenses = useMemo(() => {
    const data = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86400000);
      const key = d.toISOString().slice(0, 10);
      const label = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
      const total = expenses.filter(e => (e.date || "").startsWith(key)).reduce((s, e) => s + Number(e.amount), 0);
      data.push({ name: label, amount: total });
    }
    return data;
  }, [expenses]);

  // Top spenders (by expense total per team member)
  const topSpenders = useMemo(() => {
    if (!isAdmin) return [];
    const map = {};
    expenses.forEach(e => {
      const key = e.assignedTo || "admin";
      map[key] = (map[key] || 0) + Number(e.amount);
    });
    const members = Object.entries(map).map(([id, total]) => {
      const member = team.find(m => m.id === id);
      return { name: member ? member.name : (id === "admin" ? "Admin" : id.slice(0, 8)), total };
    });
    return members.sort((a, b) => b.total - a.total).slice(0, 6);
  }, [expenses, team, isAdmin]);

  const handleExport = () => {
    const rows = [["Date", "Category", "Amount", "Description", "Payment Method"]];
    expenses.forEach(e => {
      rows.push([e.date, e.category, e.amount, e.description, e.paymentMethod || "Cash"]);
    });
    const csv = rows.map(r => r.map(c => '"' + String(c || "").replace(/"/g, '""') + '"').join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "finboard-analytics-" + new Date().toISOString().slice(0, 10) + ".csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: "24px", maxWidth: 1200, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: "#E2E8F0", marginBottom: 4 }}>Analytics & Reports</h2>
          <p style={{ fontSize: 13, color: "#64748B" }}>Financial overview and insights</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {["3m", "6m", "12m"].map(p => (
            <button key={p} onClick={() => setPeriod(p)} style={{
              padding: "8px 16px", borderRadius: 8, border: "1px solid " + (period === p ? "var(--gold)" : "var(--border2)"),
              background: period === p ? "rgba(245,166,35,.12)" : "var(--glass)", color: period === p ? "#F5A623" : "#94A3B8",
              fontSize: 13, fontWeight: 600, cursor: "pointer"
            }}>{p.toUpperCase()}</button>
          ))}
          <button onClick={handleExport} style={{
            padding: "8px 16px", borderRadius: 8, border: "1px solid var(--border2)",
            background: "var(--glass)", color: "#94A3B8", fontSize: 13, fontWeight: 600,
            cursor: "pointer", display: "flex", alignItems: "center", gap: 6
          }}><Download size={14} /> Export</button>
        </div>
      </div>

      {/* Top Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
        <StatCard icon={<DollarSign size={20} />} label="Total Expenses" value={formatINR(expenseStats.total)} sub={expenses.length + " transactions"} color="#F5A623" />
        <StatCard icon={Number(expenseStats.change) >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />} label="This Month" value={formatINR(expenseStats.monthTotal)} sub={expenseStats.change > 0 ? "+" + expenseStats.change + "% vs last month" : expenseStats.change + "% vs last month"} color={Number(expenseStats.change) >= 0 ? "#EF4444" : "#10B981"} />
        {isAdmin && <StatCard icon={<Users size={20} />} label="Active Team" value={teamStats.activeCount} sub={"of " + teamStats.totalCount + " total"} color="#3B82F6" />}
        {isAdmin && <StatCard icon={<CreditCard size={20} />} label="Total Payroll Paid" value={formatINR(teamStats.totalPaid)} sub={payments.length + " payments"} color="#A78BFA" />}
        {isAdmin && <StatCard icon={<Landmark size={20} />} label="Loans Approved" value={formatINR(loanStats.totalApproved)} sub={loanStats.approved + " approved, " + loanStats.pending + " pending"} color="#10B981" />}
      </div>

      {/* Monthly Trend Chart */}
      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <SectionTitle><BarChart3 size={18} style={{ color: "#F5A623" }} /> Monthly Expense vs Payroll Trend</SectionTitle>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={monthlyTrend} barGap={4}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
            <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
            <YAxis stroke="#64748B" fontSize={12} tickFormatter={v => v >= 1000 ? (v / 1000).toFixed(0) + "k" : v} />
            <Tooltip {...tooltipStyle} formatter={(v) => formatINR(v)} />
            <Legend wrapperStyle={{ fontSize: 12, color: "#94A3B8" }} />
            <Bar dataKey="expenses" name="Expenses" fill="#F5A623" radius={[4, 4, 0, 0]} />
            <Bar dataKey="payroll" name="Payroll" fill="#3B82F6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Two Column: Category Pie + Daily Area */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
        {/* Category Breakdown */}
        <div className="card" style={{ padding: 24 }}>
          <SectionTitle><PieIcon size={18} style={{ color: "#10B981" }} /> Expense by Category</SectionTitle>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={categoryData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="value">
                  {categoryData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip {...tooltipStyle} formatter={(v) => formatINR(v)} />
                <Legend wrapperStyle={{ fontSize: 11, color: "#94A3B8" }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center", color: "#475569", fontSize: 13 }}>No expense data yet</div>
          )}
        </div>

        {/* Daily Expense Trend */}
        <div className="card" style={{ padding: 24 }}>
          <SectionTitle><TrendingUp size={18} style={{ color: "#22D3EE" }} /> Daily Expenses (30 Days)</SectionTitle>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={dailyExpenses}>
              <defs>
                <linearGradient id="expGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22D3EE" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#22D3EE" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.06)" />
              <XAxis dataKey="name" stroke="#64748B" fontSize={10} interval={4} />
              <YAxis stroke="#64748B" fontSize={11} tickFormatter={v => v >= 1000 ? (v / 1000).toFixed(0) + "k" : v} />
              <Tooltip {...tooltipStyle} formatter={(v) => formatINR(v)} />
              <Area type="monotone" dataKey="amount" stroke="#22D3EE" fillOpacity={1} fill="url(#expGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Admin-only sections */}
      {isAdmin && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
          {/* Payment Method Breakdown */}
          <div className="card" style={{ padding: 24 }}>
            <SectionTitle><CreditCard size={18} style={{ color: "#A78BFA" }} /> Payment Methods</SectionTitle>
            {paymentMethodData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={paymentMethodData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, percent }) => name + " " + (percent * 100).toFixed(0) + "%"}>
                    {paymentMethodData.map((_, i) => <Cell key={i} fill={COLORS[(i + 3) % COLORS.length]} />)}
                  </Pie>
                  <Tooltip {...tooltipStyle} formatter={(v) => formatINR(v)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 260, display: "flex", alignItems: "center", justifyContent: "center", color: "#475569", fontSize: 13 }}>No data</div>
            )}
          </div>

          {/* Loan Status */}
          <div className="card" style={{ padding: 24 }}>
            <SectionTitle><Landmark size={18} style={{ color: "#F97316" }} /> Loan Requests Overview</SectionTitle>
            {loanChartData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={loanChartData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={4} dataKey="value">
                      {loanChartData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip {...tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: "flex", justifyContent: "center", gap: 20, marginTop: 8 }}>
                  {loanChartData.map(d => (
                    <div key={d.name} style={{ textAlign: "center" }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: d.color, margin: "0 auto 4px" }} />
                      <div style={{ fontSize: 11, color: "#94A3B8" }}>{d.name}</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "#E2E8F0" }}>{d.value}</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ height: 260, display: "flex", alignItems: "center", justifyContent: "center", color: "#475569", fontSize: 13 }}>No loan requests</div>
            )}
          </div>
        </div>
      )}

      {/* Top Spenders Table (admin only) */}
      {isAdmin && topSpenders.length > 0 && (
        <div className="card" style={{ padding: 24, marginBottom: 24 }}>
          <SectionTitle><FileText size={18} style={{ color: "#F5A623" }} /> Top Expense Contributors</SectionTitle>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["#", "Name", "Total Spent", "Share"].map(h => (
                    <th key={h} style={{ padding: "10px 14px", fontSize: 10, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: 1.5, textAlign: "left", borderBottom: "1px solid var(--border)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topSpenders.map((sp, i) => {
                  const share = expenseStats.total > 0 ? (sp.total / expenseStats.total * 100).toFixed(1) : 0;
                  return (
                    <tr key={i} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "12px 14px", fontWeight: 700, color: "#F5A623", fontSize: 14 }}>{i + 1}</td>
                      <td style={{ padding: "12px 14px", fontWeight: 600, color: "#E2E8F0", fontSize: 13 }}>{sp.name}</td>
                      <td style={{ padding: "12px 14px", fontFamily: "var(--mono)", color: "#E2E8F0", fontSize: 13 }}>{formatINR(sp.total)}</td>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ flex: 1, height: 6, background: "var(--glass2)", borderRadius: 3, overflow: "hidden", maxWidth: 120 }}>
                            <div style={{ width: share + "%", height: "100%", background: COLORS[i % COLORS.length], borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 12, color: "#94A3B8", fontWeight: 600 }}>{share}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary Footer */}
      <div className="card" style={{ padding: 20, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Calendar size={16} style={{ color: "#64748B" }} />
          <span style={{ fontSize: 12, color: "#64748B" }}>Report generated: {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
        </div>
        <div style={{ fontSize: 11, color: "#475569" }}>FinBoard Analytics v1.0</div>
      </div>
    </div>
  );
}
